(() => {
  'use strict';

  const root = document.getElementById('cg-night-guide-root');
  if (!root || root.dataset.comparisonLifecycle === 'watching') return;
  if (root.dataset.comparisonLifecycle === 'settled' && root.querySelector('.cg-night-compare')) return;

  let observer;
  let reconcileFrame = 0;
  let settleFrame = 0;
  let finalFrame = 0;
  let finished = false;
  root.dataset.comparisonLifecycle = 'watching';

  function stopWatching() {
    if (finished) return;
    finished = true;
    observer?.disconnect();
    window.cancelAnimationFrame(reconcileFrame);
    window.cancelAnimationFrame(settleFrame);
    window.cancelAnimationFrame(finalFrame);
    window.removeEventListener('load', settleAfterLoad);
    root.dataset.comparisonLifecycle = 'settled';
    root.dataset.comparisonEnhancement = root.querySelector('.cg-night-compare') ? 'ready' : 'unavailable';
  }

  function mountComparison() {
    const section = root.querySelector('#the-glare');
    if (!section) return false;
    if (section.querySelector('.cg-night-compare')) {
      root.dataset.comparisonEnhancement = 'ready';
      return true;
    }

    const sourceImage = section.querySelector('.editorial-panorama img');
    const sourceUrl = window.CAMGUARD_NIGHT_ASSETS?.['04-window-glare.webp'] || sourceImage?.src || sourceImage?.currentSrc;
    if (!sourceUrl) return false;

    const comparison = document.createElement('figure');
    comparison.className = 'cg-night-compare';
    comparison.innerHTML = `
      <figcaption class="cg-night-compare__intro">
        <div><span class="cg-night-compare__kicker">Interactive setup illustration</span><h3>See what the pane adds.</h3></div>
        <p>Move the divider to see how a reflection can obscure a night scene. A camera behind glass can pick up reflections from the room.</p>
      </figcaption>
      <div class="cg-night-compare__stage">
        <div class="cg-night-compare__layer cg-night-compare__layer--outside"><img alt="" aria-hidden="true"></div>
        <div class="cg-night-compare__layer cg-night-compare__layer--glass"><img alt="" aria-hidden="true"></div>
        <span class="cg-night-compare__label cg-night-compare__label--glass">Reflection illustrated</span>
        <span class="cg-night-compare__label cg-night-compare__label--outside">Original illustration</span>
        <span class="cg-night-compare__divider" aria-hidden="true"></span>
      </div>
      <div class="cg-night-compare__controls">
        <label>Move the comparison divider</label>
        <input type="range" min="8" max="92" value="50" step="1" aria-label="Reveal more or less of the behind-glass illustration">
        <output aria-live="off">50% glass view</output>
      </div>
      <p class="cg-night-compare__note">The same original editorial illustration appears on both sides. The reflection overlay explains the setup idea; it is not camera footage or a measured performance test.</p>`;

    comparison.querySelectorAll('img').forEach(image => {
      image.src = sourceUrl;
      image.width = sourceImage?.naturalWidth || 1600;
      image.height = sourceImage?.naturalHeight || 900;
      image.loading = 'lazy';
      image.decoding = 'async';
    });

    const range = comparison.querySelector('input[type="range"]');
    const label = comparison.querySelector('label');
    const output = comparison.querySelector('output');
    const rangeId = 'cg-night-compare-control';
    range.id = rangeId;
    label.htmlFor = rangeId;
    output.setAttribute('for', rangeId);
    const update = () => {
      comparison.style.setProperty('--compare-position', `${range.value}%`);
      output.textContent = `${range.value}% glass view`;
      range.setAttribute('aria-valuetext', `${range.value} percent of the behind-glass illustration revealed`);
    };
    range.addEventListener('input', update, {passive: true});
    const stage = comparison.querySelector('.cg-night-compare__stage');
    let pointer = null;
    const dragDivider = (event) => {
      if (pointer !== event.pointerId) return;
      const bounds = stage.getBoundingClientRect();
      range.value = String(Math.round(Math.max(8, Math.min(92, 100 * (event.clientX - bounds.left) / bounds.width))));
      update();
    };
    stage.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      pointer = event.pointerId;
      stage.setPointerCapture(pointer);
      dragDivider(event);
    });
    stage.addEventListener('pointermove', dragDivider);
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => stage.addEventListener(type, () => { pointer = null; }));
    stage.addEventListener('dragstart', (event) => event.preventDefault());
    update();

    const anchor = section.querySelector('.diagram-explainer') || section.querySelector('blockquote');
    section.insertBefore(comparison, anchor || null);
    root.dataset.comparisonEnhancement = 'ready';
    return true;
  }

  function queueReconcile() {
    if (finished || reconcileFrame) return;
    reconcileFrame = window.requestAnimationFrame(() => {
      reconcileFrame = 0;
      mountComparison();
    });
  }

  function settleAfterLoad() {
    if (finished || settleFrame) return;
    // React schedules createRoot().render() before this enhancer. Two frames after
    // load give that commit and its MutationObserver record time to complete.
    settleFrame = window.requestAnimationFrame(() => {
      settleFrame = 0;
      finalFrame = window.requestAnimationFrame(() => {
        finalFrame = 0;
        mountComparison();
        stopWatching();
      });
    });
  }

  // Observe before the first mount. If React replaces the server-rendered article,
  // the same observer remounts into the committed #the-glare section.
  observer = new MutationObserver(queueReconcile);
  observer.observe(root, {childList: true, subtree: true});
  mountComparison();

  if (document.readyState === 'complete') settleAfterLoad();
  else window.addEventListener('load', settleAfterLoad, {once: true});
  window.addEventListener('pagehide', stopWatching, {once: true});
})();
